export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  readonly error?: { readonly code?: string; readonly message?: string };
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ErrorBody;
    throw new ApiError(
      response.status,
      body.error?.code ?? 'HTTP_ERROR',
      body.error?.message ?? response.statusText,
    );
  }
  return (await response.json()) as T;
}

/** GET a JSON endpoint of our API; non-2xx responses become ApiError with the server code. */
export async function getJson<T>(path: string): Promise<T> {
  return parseResponse<T>(await fetch(path, { headers: { accept: 'application/json' } }));
}

/**
 * POST to our API (same error handling as getJson). The JSON content type is only sent with a
 * body: Fastify rejects an empty body declared as JSON (400), which broke the payment result page.
 */
export async function postJson<T>(path: string, body?: unknown): Promise<T> {
  const request: RequestInit =
    body === undefined
      ? { method: 'POST', headers: { accept: 'application/json' } }
      : {
          method: 'POST',
          headers: { accept: 'application/json', 'content-type': 'application/json' },
          body: JSON.stringify(body),
        };
  return parseResponse<T>(await fetch(path, request));
}

/** PUT JSON to our API (same error handling as getJson). */
export async function putJson<T>(path: string, body: unknown): Promise<T> {
  return parseResponse<T>(
    await fetch(path, {
      method: 'PUT',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}
